import React, { Suspense, useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { CanvasWrapper, GridBox } from "./components";
import { MODELS, getModelAvailableSides } from "./constants/models";
import {
  buildReviewUrl,
  decodeDesignToken,
  encodeDesignToken,
  getDesignEntriesForSide,
  hasDesignObjects,
  serializeDesign,
} from "./constants/designState";
import { MODEL_PREVIEW_DELAY_MS } from "./constants/motion";
import { getModelSideImageUrl } from "./constants/publicAssetUrls";
// import { SIDE_IMAGE_MEASUREMENTS } from "./constants/imageMeasurements";
// import { getCanvasToModelConversion } from "./constants/modelMeasurements";
import ModelScreenshotCapture from "./tools/ModelScreenshotCapture";
import * as THREE from "three";
import './App.css';

const ROTATE_SPEED = 0.05; // Adjust this value to make the rotation faster or slower
const ARRIVAL_THRESHOLD = 0.01; // How close the camera needs to be to the target radius & height to consider it "arrived"
const ANGLE_THRESHOLD = 0.02; // How close the angle needs to be to the target angle to consider the camera "arrived"
const SIDE_LABELS = {
  front: 'Front',
  back: 'Back',
  left: 'Left',
  right: 'Right',
  top: 'Top',
};

const getInitialStudioConfig = () => {
  const params = new URL(window.location.href).searchParams;
  const design = decodeDesignToken(params.get('design'));
  const requestedModelName = params.get('model') ?? design?.m;
  const model = MODELS.find((candidate) => candidate.name === requestedModelName) ?? MODELS[0];

  return {
    design: design?.m === model.name ? design : null,
    isEmbedded: params.get('embed') === 'shopify',
    isReview: params.get('review') === '1',
    model,
  };
};

function ModelLighting() {
  const { camera } = useThree();
  const keyLight = useRef();
  const fillLight = useRef();
  const rimLight = useRef();
  const cameraDirection = useRef(new THREE.Vector3());
  const cameraRight = useRef(new THREE.Vector3());
  const cameraUp = useRef(new THREE.Vector3());

  useFrame(() => {
    const direction = cameraDirection.current.copy(camera.position).normalize();
    const right = cameraRight.current.set(1, 0, 0).applyQuaternion(camera.quaternion);
    const up = cameraUp.current.set(0, 1, 0).applyQuaternion(camera.quaternion);

    keyLight.current?.position
      .copy(direction)
      .multiplyScalar(3)
      .addScaledVector(right, -1.75)
      .addScaledVector(up, 2.25);
    fillLight.current?.position
      .copy(direction)
      .multiplyScalar(2.5)
      .addScaledVector(right, 2)
      .addScaledVector(up, 0.75);
    rimLight.current?.position
      .copy(direction)
      .multiplyScalar(-2.5)
      .addScaledVector(right, 0.5)
      .addScaledVector(up, 2);
  });

  return (
    <>
      <ambientLight intensity={0.2} />
      <hemisphereLight args={['#ffffff', '#6b5e56', 0.65]} />
      <directionalLight
        ref={keyLight}
        castShadow
        intensity={2}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-near={0.1}
        shadow-camera-far={10}
        shadow-camera-left={-1.2}
        shadow-camera-right={1.2}
        shadow-camera-top={1.2}
        shadow-camera-bottom={-1.2}
        shadow-bias={-0.0002}
        shadow-normalBias={0.005}
      />
      <directionalLight ref={fillLight} intensity={0.55} />
      <directionalLight ref={rimLight} intensity={0.8} />
    </>
  );
}

const getCurrentCameraPosition = (pos, target, axisOfRotation) => {
  const dx = pos.x - target.x;
  const dy = pos.y - target.y; 
  const dz = pos.z - target.z;

  if (axisOfRotation === 'x') { // for the keychain
    return {
      actualAngle: Math.atan2(dy, dz),
      actualRadius: Math.sqrt(dy * dy + dz * dz),
      actualOrbitCenter: pos.x
    };
  } else { // everything else
    return {
      actualAngle: Math.atan2(dx, dz),
      actualRadius: Math.sqrt(dx * dx + dz * dz),
      actualOrbitCenter: pos.y
    };
  }
}

// 
const getCameraOrbit = (model) => {
  const target = model.cameraTarget.clone();
  const offset = model.cameraPosition.clone().sub(target);

  return {
    target,
    radius: Math.sqrt(offset.x * offset.x + offset.z * offset.z),
    heightOffset: model.cameraPosition.y - target.y,
  };
};


const getCameraViewForSide = (model, side) => {
  const target = model.cameraTargetsBySide?.[side]?.clone() ?? model.cameraTarget.clone();
  const positionOverride = model.cameraPositionsBySide?.[side];

  if (positionOverride) {
    return {
      position: positionOverride.clone(),
      target,
    };
  }

  const { radius, heightOffset } = getCameraOrbit(model);
  const angle = model.sideAngles?.[side] ?? 0;

  return {
    position: new THREE.Vector3(
      target.x + Math.sin(angle) * radius,
      target.y + heightOffset,
      target.z + Math.cos(angle) * radius
    ),
    target,
  };
};

function CameraUpdater({ selectedModel, selectedSide }) {
  const { camera } = useThree();

  useEffect(() => {
    const availableSides = getModelAvailableSides(selectedModel);
    const nextSide = availableSides.includes(selectedSide) ? "front" : availableSides[0];
    const { position, target } = getCameraViewForSide(selectedModel, nextSide);
    camera.position.set(...position);
    camera.up.copy(selectedModel.cameraUpBySide?.[nextSide] ?? THREE.Object3D.DEFAULT_UP);
    camera.lookAt(target);
    camera.fov = selectedModel.fov;
    camera.updateProjectionMatrix();
  }, [camera, selectedModel]);

  return null;
}

function CameraController({ orbitRef, isAnimating, cameraAngle, targetAngle, model, selectedSide, onAnimationComplete }) {
  const { camera } = useThree();
  const targetView = getCameraViewForSide(model, selectedSide);
  const targetOffset = targetView.position.clone().sub(targetView.target);
  const targetRadius = model.axisOfRotation === "x"
    ? Math.sqrt(targetOffset.y ** 2 + targetOffset.z ** 2)
    : Math.sqrt(targetOffset.x ** 2 + targetOffset.z ** 2);
  const targetOrbitCenter = targetView.position[model.axisOfRotation];

  //console.log("yeet: ", targetOrbitCenter);
  const currentRadius = useRef(targetRadius);
  const currentOrbitCenter = useRef(targetOrbitCenter);

  useFrame(() => {
    if (!orbitRef.current || !isAnimating.current) return;

    camera.up.copy(model.cameraUpBySide?.[selectedSide] ?? THREE.Object3D.DEFAULT_UP);

    // Get the current camera position
    const { actualRadius, actualOrbitCenter } = getCurrentCameraPosition(camera.position, 
      orbitRef.current.target, model.axisOfRotation);

    // A top view sits directly on the orbit pole, where an angle/radius orbit
    // becomes degenerate. Interpolate the complete camera vector while moving
    // into or away from that pole so Top transitions remain smooth and the
    // established four horizontal views keep their existing orbit behavior.
    if (selectedSide === 'top' || actualRadius < 0.05) {
      camera.position.lerp(targetView.position, ROTATE_SPEED);
      orbitRef.current.target.lerp(targetView.target, ROTATE_SPEED);
      orbitRef.current.update();

      if (
        camera.position.distanceTo(targetView.position) < ARRIVAL_THRESHOLD &&
        orbitRef.current.target.distanceTo(targetView.target) < ARRIVAL_THRESHOLD
      ) {
        camera.position.copy(targetView.position);
        orbitRef.current.target.copy(targetView.target);
        orbitRef.current.update();
        isAnimating.current = false;
      }
      return;
    }

    // Increment the camera position towards the target position
    cameraAngle.current += (targetAngle.current - cameraAngle.current) * ROTATE_SPEED *1.5;
    currentRadius.current = actualRadius + (targetRadius- actualRadius) * ROTATE_SPEED;
    currentOrbitCenter.current = actualOrbitCenter + (targetOrbitCenter - actualOrbitCenter) * ROTATE_SPEED;

    if (model.axisOfRotation === "x") {
      camera.position.set(
        currentOrbitCenter.current,
        Math.sin(cameraAngle.current) * currentRadius.current,
        Math.cos(cameraAngle.current) * currentRadius.current
      );
    } else {
      camera.position.set(
        Math.sin(cameraAngle.current) * currentRadius.current,
        currentOrbitCenter.current,
        Math.cos(cameraAngle.current) * currentRadius.current
      );
    }

    // Smoothly interpolate the target point as well for a more natural orbiting effect
    orbitRef.current.target.lerp(targetView.target, ROTATE_SPEED);
    orbitRef.current.update();

    // Check if we've arrived at the target position, within a given threshold
    const angleDone = Math.abs(targetAngle.current - cameraAngle.current) < ANGLE_THRESHOLD;
    const radiusDone = Math.abs(targetRadius - currentRadius.current) < ARRIVAL_THRESHOLD;
    const orbitCenterDone = Math.abs(targetOrbitCenter - currentOrbitCenter.current) < ARRIVAL_THRESHOLD;

    if (angleDone && radiusDone && orbitCenterDone) {
      cameraAngle.current = targetAngle.current;
      isAnimating.current = false;
    }
  });

  return null;
}

function App() {
  const initialConfig = useRef(getInitialStudioConfig()).current;
  const [selectedModel, setSelectedModel] = useState(initialConfig.model);
  const [showCaptureTool, setShowCaptureTool] = useState(
    () => new URL(window.location.href).searchParams.get('capture') === '1'
  );
  const [canvasRefs, setCanvasRefs] = useState({});
  const [selectedSide, setSelectedSide] = useState('front');
  const [visibleSide, setVisibleSide] = useState('front');
  const [modelSize, setModelSize] = useState(null);
  const [isEditingBrand, setIsEditingBrand] = useState(false);
  const [readyModelId, setReadyModelId] = useState(null);
  const [showModelPreparation, setShowModelPreparation] = useState(false);
  const [sidePanelHeight, setSidePanelHeight] = useState(865);
  const sidePanelElements = useRef({});
  const orbitRef = useRef();
  const isAnimating = useRef(false);
  const cameraAngle = useRef(0);
  const targetAngle = useRef(0);
  const availableSides = getModelAvailableSides(selectedModel);
  const isModelReady = readyModelId === selectedModel.id;
  const handleModelReady = useCallback((modelId) => {
    setReadyModelId(modelId);
    setShowModelPreparation(false);
  }, []);

  const setCanvasRef = useCallback((side, canvas) => {
    setCanvasRefs((currentRefs) => ({
      ...currentRefs,
      [side]: canvas,
    }));
  }, []);

  // Update state variables when model changes
  useEffect(() => { 
    const nextSide = availableSides.includes(selectedSide) ? "front" : availableSides[0];
    setCanvasRefs({});
    setIsEditingBrand(false);
    setSelectedSide(nextSide);
    setVisibleSide(nextSide);
  }, [selectedModel]);

  useEffect(() => {
    setShowModelPreparation(false);
    const preparationTimer = window.setTimeout(() => {
      setShowModelPreparation(true);
    }, MODEL_PREVIEW_DELAY_MS);

    return () => window.clearTimeout(preparationTimer);
  }, [selectedModel.id]);

  useEffect(() => {
    const design = serializeDesign(canvasRefs, selectedModel);
    const isCustomized = hasDesignObjects(design);
    const designToken = isCustomized ? encodeDesignToken(design) : '';
    const reviewUrl = isCustomized
      ? buildReviewUrl({ designToken, modelName: selectedModel.name })
      : '';

    const legacyBrandsField = document.getElementById('brands');
    if (legacyBrandsField) {
      legacyBrandsField.value = reviewUrl;
      legacyBrandsField.textContent = reviewUrl;
    }

    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'everwest:design-change',
        designToken,
        isCustomized,
        modelName: selectedModel.name,
        reviewUrl,
        schemaVersion: design.v,
      }, '*');
    }
  }, [canvasRefs, selectedModel]);

  useEffect(() => {
    if (window.parent === window) {
      return undefined;
    }

    const studioElement = document.querySelector('.app-shell');
    const postHeight = () => {
      // Measuring the document height creates a feedback loop inside an iframe:
      // once the parent makes the iframe taller, documentElement.scrollHeight
      // also becomes taller. The app shell is the intrinsic studio content and
      // remains independent of the iframe viewport.
      const height = Math.ceil(studioElement?.getBoundingClientRect().height ?? 0);
      if (height <= 0) {
        return;
      }
      window.parent.postMessage({ type: 'everwest:resize', height }, '*');
    };

    window.parent.postMessage({
      type: 'everwest:ready',
      modelName: selectedModel.name,
    }, '*');
    postHeight();

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(postHeight);
    if (studioElement) {
      resizeObserver?.observe(studioElement);
    }
    window.addEventListener('load', postHeight);
    window.addEventListener('resize', postHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('load', postHeight);
      window.removeEventListener('resize', postHeight);
    };
  }, [selectedModel.name]);

  useLayoutEffect(() => {
    const visiblePanel = sidePanelElements.current[visibleSide];
    if (!visiblePanel) {
      return undefined;
    }

    const updatePanelHeight = () => {
      const nextHeight = Math.ceil(visiblePanel.scrollHeight);
      if (nextHeight > 0) {
        setSidePanelHeight(nextHeight);
      }
    };

    updatePanelHeight();
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updatePanelHeight);
    resizeObserver?.observe(visiblePanel);
    window.addEventListener('resize', updatePanelHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updatePanelHeight);
    };
  }, [selectedModel.id, visibleSide]);

  const handleSideClick = (side) => {
    if (!availableSides.includes(side)) {
      return;
    }

    setSelectedSide(side);
    setVisibleSide(side);

    if ( orbitRef.current) {
      // Sync current angle from camera position first
      const { actualAngle } = getCurrentCameraPosition(orbitRef.current.object.position, 
        orbitRef.current.target, selectedModel.axisOfRotation);
      cameraAngle.current = actualAngle;    

      // Then compute target angle
      const rawTarget = selectedModel.sideAngles?.[side] ?? 0;
      let delta = rawTarget - cameraAngle.current;
      while (delta > Math.PI) delta -= 2 * Math.PI;
      while (delta < -Math.PI) delta += 2 * Math.PI;
      targetAngle.current = cameraAngle.current + delta;
      //console.log(actualAngle);
      //console.log(targetAngle.current);
    } 

    isAnimating.current = true;
  };

  const formatMeasurement = (value) => value.toFixed(3);
  const formatConversion = (value) => {
    if (value === 0) {
      return '0';
    }

    if (Math.abs(value) < 0.001) {
      return value.toFixed(6);
    }

    return value.toFixed(4);
  };

  return (
    <div className={`app-shell${initialConfig.isEmbedded ? ' app-shell--embedded' : ''}${initialConfig.isReview ? ' app-shell--review' : ''}`}>
      <button
        type="button"
        style={{ alignSelf: 'flex-start', borderRadius: 0, borderWidth: 1, padding: '8px 10px', background: '#eee', marginTop: 10, display: 'none'}}
        onClick={() => setShowCaptureTool((isVisible) => !isVisible)}
      >
        {showCaptureTool ? 'Hide Capture Tool' : 'Show Capture Tool'}
      </button>
      {showCaptureTool ? <ModelScreenshotCapture /> : null}
      {!initialConfig.isEmbedded && !initialConfig.isReview ? (
        <>
          <h4>Select a product model:</h4>
          <div className="product-selector">
            {MODELS.map((model) => (
              <button
                key={model.id}
                type="button"
                className={`studio-control product-selector__button${selectedModel.id === model.id ? ' is-selected' : ''}`}
                aria-pressed={selectedModel.id === model.id}
                onClick={() => setSelectedModel(model)}
              >
                {model.name.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </>
      ) : null}
      <h2 className="workflow-title">Customize Your Gear</h2>

      <section className="workflow-section" aria-labelledby="choose-side-heading">
        <h3 id="choose-side-heading" className="workflow-heading"><span className="workflow-step">01</span> Choose a Side</h3>
        <p className="workflow-description">Select the side you'd like to customize</p>
        <div className="side-selector">
          {availableSides.map((side) => (
              <button
                key={side}
                type="button"
                className={`studio-control side-selector__button${selectedSide === side ? ' side-selector__button--selected' : ''}`}
                aria-pressed={selectedSide === side}
                onClick={() => handleSideClick(side)}
              >
                {SIDE_LABELS[side]}
              </button>
            ))}
        </div>
      </section>

      <div className="side-panel-stack" style={{ height: `${sidePanelHeight}px` }}>
        {availableSides.map((side) => (
          <div
            key={side}
            ref={(element) => {
              if (element) {
                sidePanelElements.current[side] = element;
              } else {
                delete sidePanelElements.current[side];
              }
            }}
            className={`side-panel${visibleSide === side ? ' side-panel--visible' : ''}`}
            aria-hidden={visibleSide !== side}
          >
            <CanvasWrapper
              key={`${selectedModel.id}-${side}`}
              selectedModel={selectedModel}
              setRef={(canvas) => setCanvasRef(side, canvas)}
              onBrandInteractionChange={setIsEditingBrand}
              className={`canvas-${side}`}
              side={side}
              sideImagePath={selectedModel.name + `/${side}.png`}
              initialDesignEntries={getDesignEntriesForSide(initialConfig.design, side)}
            />
          </div>
        ))}
      </div>

      <section className="workflow-section" aria-labelledby="preview-gear-heading">
        <h3 id="preview-gear-heading" className="workflow-heading"><span className="workflow-step">04</span> Preview Your Gear</h3>
        <p className="workflow-description">Explore your custom gear in 3D. Rotate and zoom to review it from every angle.</p>
        <div className="model-stage">
          <div className={`model-stage__canvas${isModelReady ? ' is-ready' : ''}`}>
            <Canvas shadows="soft"
              style={{ width: '100%', height: '100%', background: '#ddd' }}>
            <CameraUpdater selectedModel={selectedModel} selectedSide={selectedSide}/>
            <ModelLighting />
            <Suspense fallback={null}>
              <GridBox
                key={selectedModel.id}
                model={selectedModel}
                canvases={canvasRefs}
                isEditingBrand={isEditingBrand}
                onModelReady={handleModelReady}
                onModelSizeChange={setModelSize}
              />
            </Suspense>
            <CameraController
              orbitRef={orbitRef}
              isAnimating={isAnimating}
              cameraAngle={cameraAngle}
              targetAngle={targetAngle}
              model={selectedModel}
              selectedSide={selectedSide}
            />
            <OrbitControls
              ref={orbitRef}
              target={[0, 0, 0]}
              enableDamping
              dampingFactor={0.08}
              zoomSpeed={0.35}
            />
            </Canvas>
          </div>
          <div
            className={`model-stage__preparation${showModelPreparation && !isModelReady ? ' is-visible' : ''}`}
            aria-live="polite"
            aria-hidden={!showModelPreparation || isModelReady}
          >
            <div
              className="model-stage__reference"
              style={{
                backgroundImage: `url("${getModelSideImageUrl(`${selectedModel.name}/front.png`)}")`,
              }}
            />
            <span>Preparing your 3D preview…</span>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;
